import React, { memo } from 'react';
import { Box, Skeleton, useTheme } from '@ublend-npm/aulaui-core';
import { getSpaceWidgetColors } from './spaceWidgetColors';
import {
  SPACE_CARD_BORDER_RADIUS,
  SPACE_CARD_BODY_BLOCK_GAP_PX,
  SPACE_CARD_BODY_BLOCK_HEIGHT_PX,
  SPACE_CARD_DESCRIPTION_HEIGHT_PX,
  SPACE_CARD_HEADER_ROW_GAP_PX,
  SPACE_CARD_HEADER_ROW_HEIGHT_PX,
  SPACE_CARD_HEIGHT_PX,
  SPACE_CARD_ICON_BUTTON_SIZE_PX,
  SPACE_CARD_INNER_HEIGHT_PX,
  SPACE_CARD_INNER_WIDTH_PX,
  SPACE_CARD_PADDING_X_PX,
  SPACE_CARD_PADDING_Y_PX,
  SPACE_CARD_PROGRESS_SECTION_GAP_PX,
  SPACE_CARD_PROGRESS_SECTION_HEIGHT_PX,
  SPACE_CARD_SHADOW,
  SPACE_CARD_SKELETON_SURFACE,
  SPACE_CARD_TEXT_BLOCK_GAP_PX,
  SPACE_CARD_TEXT_BLOCK_HEIGHT_PX,
  SPACE_CARD_TITLE_HEIGHT_PX,
  SPACE_CARD_WIDTH_PX,
  SPACE_META_PILL_HEIGHT_PX,
  SPACE_META_PILLS_CONTAINER_HEIGHT_PX,
  SPACE_META_PILL_GAP_PX,
  SPACE_PROGRESS_BAR_HEIGHT_PX,
  SPACE_PROGRESS_LABEL_HEIGHT_PX,
  SPACE_RESUME_BUTTON_HEIGHT_PX,
} from './spaceWidgetConstants';

type SpaceCardSkeletonProps = {
  skeletonSurface?: string;
};

const SpaceCardSkeleton = ({
  skeletonSurface = SPACE_CARD_SKELETON_SURFACE,
}: SpaceCardSkeletonProps) => {
  const { palette } = useTheme();
  const colors = getSpaceWidgetColors(palette);

  return (
    <Box
      aria-hidden="true"
      data-testid="space-card-skeleton"
      sx={{
        width: `${SPACE_CARD_WIDTH_PX}px`,
        minWidth: `${SPACE_CARD_WIDTH_PX}px`,
        maxWidth: `${SPACE_CARD_WIDTH_PX}px`,
        height: `${SPACE_CARD_HEIGHT_PX}px`,
        flexShrink: 0,
        borderRadius: SPACE_CARD_BORDER_RADIUS,
        bgcolor: colors.cardBg,
        boxSizing: 'border-box',
        boxShadow: SPACE_CARD_SHADOW,
        py: `${SPACE_CARD_PADDING_Y_PX}px`,
        px: `${SPACE_CARD_PADDING_X_PX}px`,
      }}
    >
      <Box
        sx={{
          width: `${SPACE_CARD_INNER_WIDTH_PX}px`,
          height: `${SPACE_CARD_INNER_HEIGHT_PX}px`,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            width: `${SPACE_CARD_INNER_WIDTH_PX}px`,
            height: `${SPACE_CARD_BODY_BLOCK_HEIGHT_PX}px`,
            display: 'flex',
            flexDirection: 'column',
            gap: `${SPACE_CARD_BODY_BLOCK_GAP_PX}px`,
            boxSizing: 'border-box',
          }}
        >
          <Box
            sx={{
              width: `${SPACE_CARD_INNER_WIDTH_PX}px`,
              height: `${SPACE_CARD_HEADER_ROW_HEIGHT_PX}px`,
              display: 'flex',
              alignItems: 'center',
              gap: `${SPACE_CARD_HEADER_ROW_GAP_PX}px`,
              flexShrink: 0,
            }}
          >
            <Skeleton
              kind="circular"
              width={SPACE_CARD_ICON_BUTTON_SIZE_PX}
              height={SPACE_CARD_ICON_BUTTON_SIZE_PX}
              sx={{ bgcolor: skeletonSurface, flexShrink: 0 }}
            />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: `${SPACE_META_PILL_GAP_PX}px`,
                flex: 1,
                minWidth: 0,
                height: `${SPACE_META_PILLS_CONTAINER_HEIGHT_PX}px`,
              }}
            >
              <Skeleton
                kind="rounded"
                width={48}
                height={SPACE_META_PILL_HEIGHT_PX}
                sx={{ borderRadius: '100px', bgcolor: skeletonSurface }}
              />
              <Skeleton
                kind="rounded"
                width={88}
                height={SPACE_META_PILL_HEIGHT_PX}
                sx={{ borderRadius: '100px', bgcolor: skeletonSurface }}
              />
            </Box>
          </Box>

          <Box
            sx={{
              width: `${SPACE_CARD_INNER_WIDTH_PX}px`,
              height: `${SPACE_CARD_TEXT_BLOCK_HEIGHT_PX}px`,
              display: 'flex',
              flexDirection: 'column',
              gap: `${SPACE_CARD_TEXT_BLOCK_GAP_PX}px`,
              boxSizing: 'border-box',
            }}
          >
            <Box
              sx={{
                width: '100%',
                height: `${SPACE_CARD_TITLE_HEIGHT_PX}px`,
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                justifyContent: 'center',
              }}
            >
              <Skeleton
                kind="rounded"
                height={20}
                sx={{
                  width: '100%',
                  borderRadius: '6px',
                  bgcolor: skeletonSurface,
                }}
              />
              <Skeleton
                kind="rounded"
                height={20}
                sx={{
                  width: '78%',
                  borderRadius: '6px',
                  bgcolor: skeletonSurface,
                }}
              />
            </Box>

            <Skeleton
              kind="rounded"
              height={SPACE_CARD_DESCRIPTION_HEIGHT_PX}
              sx={{
                width: '100%',
                borderRadius: '6px',
                bgcolor: skeletonSurface,
              }}
            />

            <Box
              sx={{
                width: '100%',
                height: `${SPACE_CARD_PROGRESS_SECTION_HEIGHT_PX}px`,
                display: 'flex',
                flexDirection: 'column',
                gap: `${SPACE_CARD_PROGRESS_SECTION_GAP_PX}px`,
                flexShrink: 0,
              }}
            >
              <Skeleton
                kind="rounded"
                height={SPACE_PROGRESS_BAR_HEIGHT_PX}
                sx={{
                  width: '100%',
                  borderRadius: '999px',
                  bgcolor: skeletonSurface,
                }}
              />
              <Skeleton
                kind="rounded"
                height={SPACE_PROGRESS_LABEL_HEIGHT_PX}
                sx={{
                  width: '42%',
                  borderRadius: '6px',
                  bgcolor: skeletonSurface,
                }}
              />
            </Box>
          </Box>
        </Box>

        <Skeleton
          kind="rounded"
          height={SPACE_RESUME_BUTTON_HEIGHT_PX}
          sx={{
            width: `${SPACE_CARD_INNER_WIDTH_PX}px`,
            borderRadius: '8px',
            bgcolor: skeletonSurface,
            flexShrink: 0,
          }}
        />
      </Box>
    </Box>
  );
};

export default memo(SpaceCardSkeleton);
